import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Input,
  Button,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Text,
  IconButton,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  InputGroup,
  InputLeftElement,
  Icon,
} from '@chakra-ui/react';
import { FiSearch, FiEdit, FiTrash2, FiEye, FiDownload } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../services/api';

const ViewPage = () => {
  const [data, setData] = useState({
    series: [] as any[],
    subjects: [] as any[],
    classes: [] as any[],
    books: [] as any[],
    bookTypes: [] as any[],
  });
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue('white', 'gray.800');

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [series, subjects, classes, books, bookTypes] = await Promise.all([
        api.getSeries(),
        api.getSubjects(),
        api.getClasses(),
        api.getBooks(),
        api.getBookTypes(),
      ]);
      
      setData({ series, subjects, classes, books, bookTypes });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  if (loading) {
    return (
      <VStack spacing={6} align="stretch">
        <Text>Loading all content...</Text>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Heading size="xl" color="gray.700">
        👁️ View All Content
      </Heading>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>📚 Series ({data.series.length})</Tab>
          <Tab>📝 Subjects ({data.subjects.length})</Tab>
          <Tab>🎓 Classes ({data.classes.length})</Tab>
          <Tab>📕 Books ({data.books.length})</Tab>
          <Tab>📁 Types ({data.bookTypes.length})</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <SeriesPanel series={data.series} />
          </TabPanel>
          <TabPanel>
            <SubjectsPanel subjects={data.subjects} />
          </TabPanel>
          <TabPanel>
            <ClassesPanel classes={data.classes} />
          </TabPanel>
          <TabPanel>
            <BooksPanel books={data.books} />
          </TabPanel>
          <TabPanel>
            <BookTypesPanel bookTypes={data.bookTypes} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
};

const SeriesPanel: React.FC<{ series: any[] }> = ({ series }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  
  return (
    <VStack spacing={4}>
      {series.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No series found. <Button as={RouterLink} to="/series" size="sm" ml={2}>Create one</Button>
        </Alert>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
          {series.map((item) => (
            <Card key={item.id} bg={cardBg}>
              <CardBody>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="bold" fontSize="lg">{item.title}</Text>
                  <Text fontSize="sm" color="gray.500">{item.name}</Text>
                  <Text fontSize="sm">{item.description}</Text>
                  <Badge colorScheme="blue">{item.subjects?.length || 0} subjects</Badge>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  );
};

const SubjectsPanel: React.FC<{ subjects: any[] }> = ({ subjects }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  
  return (
    <VStack spacing={4}>
      {subjects.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No subjects found. <Button as={RouterLink} to="/subjects" size="sm" ml={2}>Create one</Button>
        </Alert>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
          {subjects.map((item) => (
            <Card key={item.id} bg={cardBg}>
              <CardBody>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="bold" fontSize="lg">{item.title}</Text>
                  <Text fontSize="sm" color="gray.500">{item.name}</Text>
                  <Badge colorScheme="purple">{item.series?.title}</Badge>
                  <Badge colorScheme="green">{item.classes?.length || 0} classes</Badge>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  );
};

const ClassesPanel: React.FC<{ classes: any[] }> = ({ classes }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  
  return (
    <VStack spacing={4}>
      {classes.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No classes found. <Button as={RouterLink} to="/classes" size="sm" ml={2}>Create one</Button>
        </Alert>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
          {classes.map((item) => (
            <Card key={item.id} bg={cardBg}>
              <CardBody>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="bold" fontSize="lg">{item.name}</Text>
                  <Text fontSize="sm" color="gray.500">Subject: {item.subject?.title}</Text>
                  <Badge colorScheme="purple">{item.subject?.series?.title}</Badge>
                  <Badge colorScheme="orange">{item.books?.length || 0} books</Badge>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  );
};

const BooksPanel: React.FC<{ books: any[] }> = ({ books }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  
  return (
    <VStack spacing={4}>
      {books.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No books found. <Button as={RouterLink} to="/books" size="sm" ml={2}>Create one</Button>
        </Alert>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
          {books.map((item) => (
            <Card key={item.id} bg={cardBg}>
              <CardBody>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="bold" fontSize="lg">{item.name}</Text>
                  <Badge colorScheme="green">${item.price}</Badge>
                  <Text fontSize="sm" color="gray.500">Class: {item.class?.name}</Text>
                  <Text fontSize="sm" color="gray.500">Subject: {item.class?.subject?.title}</Text>
                  <Badge colorScheme="blue">{item.bookFiles?.length || 0} files</Badge>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  );
};

const BookTypesPanel: React.FC<{ bookTypes: any[] }> = ({ bookTypes }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  
  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'PDF':
        return '📄';
      case 'VIDEO':
        return '🎥';
      case 'IMAGE':
        return '🖼️';
      default:
        return '📁';
    }
  };

  return (
    <VStack spacing={4}>
      {bookTypes.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No book types found. <Button as={RouterLink} to="/book-types" size="sm" ml={2}>Create one</Button>
        </Alert>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
          {bookTypes.map((item) => (
            <Card key={item.id} bg={cardBg}>
              <CardBody>
                <VStack align="start" spacing={2}>
                  <HStack>
                    <Text>{getContentTypeIcon(item.contentType)}</Text>
                    <Text fontWeight="bold" fontSize="lg">{item.typeName}</Text>
                  </HStack>
                  <Badge colorScheme="purple">{item.contentType}</Badge>
                  {item.description && (
                    <Text fontSize="sm" color="gray.600">{item.description}</Text>
                  )}
                  <Badge colorScheme="teal">
                    {item._count?.bookFiles || 0} {(item._count?.bookFiles || 0) === 1 ? 'file' : 'files'}
                  </Badge>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  );
};

export default ViewPage; 